/* eslint-disable complexity */
/* eslint-disable max-statements */
import { requirejsOne } from '@ds/swymkit/tools'
import type { TagNavigator } from '@ds/swymkit/typings'
import type { RouteLocationNormalized } from 'vue-router'

import type { RoutesWithThreadIdParam } from '~/contexts/community/router/types'

import { router } from '~/router/router'

import { useConversationsStore } from '~/entities/container/conversation/stores/conversations'

import { useCommunitySettingsTags } from '~/features-swym/community-settings/composables/community-settings-tags'

import { useCommunityFeedMode } from '~/features-shared/community/stores/states'
import { useContentContextManager } from '~/features-shared/config-manager/composables/context-manager'
import { EContentContext } from '~/features-shared/config-manager/interfaces/contexts'
import { useTaggableContent } from '~/features-shared/content/composables/content-tags'
import { useContextContentsStore } from '~/features-shared/content/composables/context-contents-store'
import { EFeedMode } from '~/features-shared/content/stores/contents'
import { useContentsStoreV1 } from '~/features-shared/content/stores/v1-contents'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { useCommunityMemberTag } from '~/features-shared/member/composables/community-member-tag'
import {
  contentTagsMap,
  isTaggerReady,
  isUserTagsUtilsReady,
  settingsTagsMap,
} from '~/features-shared/tags/composables/tag-refs'
import type { IProxyManager } from '~/features-shared/tags/composables/tags-proxy'
import { useTagsProxy } from '~/features-shared/tags/composables/tags-proxy'
import type { ITaggable } from '~/features-shared/tags/interfaces/tags'
import { init as userTagsInit } from '~/features-shared/tags/utils/user-tags'

import { FEATURE_MANAGER_NAME } from '~/features-generic/config-manager/composables/feature-manager'

const CONTEXT_ID = '3DSWYM'
const RIGHT_PANEL_CONTEXT_ID = 'RightPanelContext'
/**
 * All routes for which tags are disabled
 */
const DISABLED_PAGES = [
  'calls',

  // TACTICAL CHANGE
  'community-thread_id-index-facet-:facet_uri',

  'rituals',
  'rituals-thread_id',
  'rituals-schedule',
  'rituals-schedule-thread_id',

  'me-bookmarks',

  'me-requests',

  'me-profile-index',
  'me-profile-index-website',
  'me-profile-index-skill',
  'me-profile-index-language',
  'me-profile-index-interest',
  'me-profile-index-experience',
  'me-profile-index-education',

  'me-activity',
  'me-activity-index-media',
  'me-activity-index',
  'me-activity-index-content_type',

  'me-security',

  'me-organization',

  'me-preferences',

  'me-recycle-bin',

  'network',
  'network-invitations',
  'network-user-thread_id-index-activity',
  'network-user-thread_id-index-network',
  'network-user-thread_id-index-profile',
  'network-user-thread_id-index',

  'admin-tenant',
  'admin-tenant-facet_name',
]
const proxyManagers = ref(new Map<string, IProxyManager>())
const activeProxyManagers = ref(new Set<string>())

interface TagOptions {
  widgetId?: string
  route: RouteLocationNormalized<RoutesWithThreadIdParam>
}

type ContextData = Pick<RouteLocationNormalized<RoutesWithThreadIdParam>, 'name' | 'params' | 'query'>

/**
 * Sets the tagger context as current
 * @param setRightPanelContext Should the context be set to the "RightPanelContext"? This context is used by facets
 */
export async function setTaggerCurrentContext(setRightPanelContext?: boolean) {
  const TagNavigator = await requirejsOne<TagNavigator>('DS/TagNavigator/TagNavigator')
  const context = setRightPanelContext ? RIGHT_PANEL_CONTEXT_ID : CONTEXT_ID

  TagNavigator.get6WTagger(context)
    .setAsCurrent()
}

/**
 * Focus on the proxy of a certain widget
 *
 * @param contextId The context to select (Is only "RightPanelContext" for now)
 * @param widgetId id of the widget to focus
 */
export async function focusProxyOnOtherContext(contextId: typeof RIGHT_PANEL_CONTEXT_ID, widgetId: string) {
  const TagNavigator = await requirejsOne<TagNavigator>('DS/TagNavigator/TagNavigator')

  TagNavigator
    .get6WTagger(contextId)
    .focusOnCC(widgetId)
}

/**
 * Function that is responsible to set the TagNavigator view as the current view in tagger
 * @returns nothing
 */
async function initTagger(tagOptions: TagOptions) {
  const TagNavigator = await requirejsOne<TagNavigator>('DS/TagNavigator/TagNavigator')
  if (TagNavigator.view === null) {
    // This timeout is needed because we have no way to know when the TagNavigator view is created. 1000 is a safe value
    setTimeout(() => {
      // change the tagger view but make sure we are still in the same context
      TagNavigator.get6WTagger(tagOptions?.widgetId || CONTEXT_ID)
        .setAsCurrent()
    }, 1000)
  }
  else {
    TagNavigator.get6WTagger(tagOptions?.widgetId || CONTEXT_ID)
      .setAsCurrent()
  }
  await userTagsInit()
  isUserTagsUtilsReady.value = true
}

export function useTagsContext(tagOptions: TagOptions) {
  const route = tagOptions?.route || useRoute()
  const feedMode = useCommunityFeedMode()

  /**
   * It returns the client for tags of a content if it exists, or else it creates it and then
   * returns it
   *
   * @param id The subject_uri of the content
   * @returns The "client" that is to be used for tags
   */
  const getContentClient = (id: SUBJECT_6W_URI) => {
    if (!contentTagsMap.value.has(id))
      contentTagsMap.value.set(id, useTaggableContent(id))

    return contentTagsMap.value.get(id)
  }

  /**
   * It returns the client for tags of a community for settings if it exists, or else it creates it and then
   * returns it
   *
   * @param id The subject_uri of the community
   * @returns The "client" that is to be used for tags
   */
  const getCommunitySettingsClient = (id: SUBJECT_6W_URI) => {
    if (!settingsTagsMap.value.has(id))
      settingsTagsMap.value.set(id, useCommunitySettingsTags(id))

    return settingsTagsMap.value.get(id)
  }

  /**
   * Method that returns the client to be used for tags based on current context data and passed options
   *
   * @param options Relevant data with which we'll determine the client to be used
   * @returns The client
   */
  const getClient = (options: ContextData) => {
    const routeName = options.name?.toString()
    if (options.query?.content)
      return getContentClient(router.getQuery(options, 'content')!)

    if (FEATURE_MANAGER_NAME.value === 'content-reader') {
      // Check if tagger has to be empty
      const noTag = options.query.noTag
      if ((noTag && noTag === 'true') || options.query.media)
        return null
      const { store } = useContextContentsStore(EContentContext.ContentReader)
      return store.value || null // if not on a feed context, ensures empty tagger
    }

    // Do not show tags for disabled pages
    if (DISABLED_PAGES.includes(routeName))
      return null

    // Targeting all member pages
    if (routeName?.endsWith('thread_id-members'))
      return useCommunityMemberTag()

    // Targeting all settings pages
    if (routeName?.endsWith('thread_id-settings') && options.params.thread_id)
      return getCommunitySettingsClient(options.params.thread_id)

    // Unfiltered DM context
    if (routeName === 'dm-thread_id-index') {
      const { isConversationFromV2 } = useConversationsStore()
      // Hide tags for RTConv
      if (isConversationFromV2(options.params.thread_id))
        return null
      return useContentsStoreV1()
    }

    // Unfiltered community/personal space timeline context
    if ([
      'community-thread_id-index',
      'me-personal-space-index',
    ].includes(routeName) && feedMode.value === EFeedMode.Timeline) {
      return useContentsStoreV1()
    }

    const { store } = useContextContentsStore(useContentContextManager().context)

    return store.value || null // if not on a feed context, ensures empty tagger
  }

  /**
   * Returns the proxy manager to be used for a particular route
   *
   * @param id The path
   * @param options Relevant options for the client
   * @returns The proxy manager
   */
  const getProxyManager = async (id: string, options: ContextData) => {
    let proxyManager = proxyManagers.value.get(id)
    const client = getClient(options) as ITaggable
    // const isEdition = options.query?.edit || false

    if (!proxyManager) {
      // Create a proxy-manager if it is not present
      proxyManager = await useTagsProxy(client, {
        id,
        // persistTags: !isEdition,
        communityUri: (options.params?.thread_id as string) || client?.community,
        proxyBasicOptions: {
          ...(tagOptions?.widgetId ? { widgetId: tagOptions.widgetId } : { contextId: CONTEXT_ID }),
        },
        onActivateProxy: () => activeProxyManagers.value.add(id),
        onDeactivateProxy: () => activeProxyManagers.value.delete(id),
      })
      proxyManagers.value.set(id, proxyManager)
    }
    else {
      // If we have a proxy-manager, then set the client because it is possible that we have new client
      proxyManager.setClient(client)
    }

    return proxyManager
  }

  /**
   * This function is responsible for activating a proxy-manager that should be activated for the passed route.
   * All other proxy-managers will be deactivated
   *
   * @param id The path
   * @param options Relevant options for getting a proxy-manager
   * @returns The active proxy-manager
   */
  const setActiveProxy = async (id: string, options: ContextData) => {
    const proxyManager = await getProxyManager(id, options)

    activeProxyManagers.value.forEach((pmId) => {
      // Deactivate if it's not the desired one
      if (pmId !== id) {
        proxyManagers.value.get(pmId)
          ?.deactivateProxy()
      }
    })

    // Activate the desired proxy-manager
    proxyManager.activateProxy()
    return proxyManager
  }

  /**
   * Function that sets a proxy-manager based on the current route only if the tagger is ready
   */
  const updateProxy = () => {
    if (!isTaggerReady.value)
      return

    setActiveProxy(route.fullPath, {
      name: route.name,
      params: route.params,
      query: route.query,
    })
  }

  whenever(() => route.fullPath, updateProxy)
  watch(feedMode, updateProxy)

  // We wait until tagger is ready
  until(isTaggerReady)
    .toBeTruthy()
    .then(() => {
      // When the tagger is finally ready, we initialize it
      return initTagger(tagOptions)
    })
    .then(() => {
      // After the initialization we set the proxy-manager
      setActiveProxy(route.fullPath, {
        name: route.name,
        params: route.params,
        query: route.query,
      })
    })

  return { updateProxy }
}
